<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import TurnIntoDropdown from "./TurnIntoDropdown.vue";
import LinkPopover from "./LinkPopover.vue";
import ColorTextPopover from "./ColorTextPopover.vue";

const props = defineProps({
  editor: { type: Object, required: true },
  defaultParticipantCode: { type: String, default: null },
});

const isVisible = ref(false);
const toolbarStyle = ref({});
let popoverOpen = false;

const activeBlockLabel = computed(() => {
  const e = props.editor;
  if (!e) return "Текст";
  if (e.isActive("heading", { level: 1 })) return "Заголовок 1";
  if (e.isActive("heading", { level: 2 })) return "Заголовок 2";
  if (e.isActive("heading", { level: 3 })) return "Заголовок 3";
  if (e.isActive("bulletList")) return "Список";
  if (e.isActive("orderedList")) return "Нум. список";
  if (e.isActive("blockquote")) return "Цитата";
  if (e.isActive("codeBlock")) return "Код";
  return "Текст";
});

function updatePosition() {
  if (!props.editor?.view?.dom) return;

  const { state, view } = props.editor;
  const { from, to, empty } = state.selection;

  if (empty || popoverOpen) {
    if (!popoverOpen) isVisible.value = false;
    return;
  }

  try {
    const start = view.coordsAtPos(from);
    const end = view.coordsAtPos(to);
    const container = view.dom.closest(".relative") || view.dom.parentElement;
    const cRect = container.getBoundingClientRect();

    // Position above the selection, centered
    const left = (start.left + end.left) / 2 - cRect.left;
    const top = start.top - cRect.top - 48;

    toolbarStyle.value = {
      top: `${Math.max(0, top)}px`,
      left: `${left}px`,
      transform: "translateX(-50%)",
    };
    isVisible.value = true;
  } catch {
    isVisible.value = false;
  }
}

function onSelectionUpdate() {
  if (popoverOpen) return;
  updatePosition();
}

function lockToolbar() {
  popoverOpen = true;
  // Auto-unlock after 5s as safety net
  setTimeout(() => {
    popoverOpen = false;
    updatePosition();
  }, 5000);
}

onMounted(() => {
  props.editor.on("selectionUpdate", onSelectionUpdate);
});

onBeforeUnmount(() => {
  props.editor.off("selectionUpdate", onSelectionUpdate);
});

function cmd(name, args) {
  if (args) {
    props.editor.chain().focus()[name](args).run();
  } else {
    props.editor.chain().focus()[name]().run();
  }
}
</script>

<template>
  <Transition name="sel-toolbar">
    <div
      v-if="isVisible"
      class="selection-toolbar"
      :style="toolbarStyle"
      @mousedown.prevent
    >
      <!-- Turn Into -->
      <TurnIntoDropdown
        :editor="editor"
        :default-participant-code="defaultParticipantCode"
      >
        <button class="sel-btn sel-btn--text">
          <span class="text-[13px] font-medium">{{ activeBlockLabel }}</span>
          <svg
            class="w-2.5 h-2.5 ml-0.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </TurnIntoDropdown>

      <div class="sel-divider" />

      <!-- Bold -->
      <button
        class="sel-btn"
        :class="{ active: editor.isActive('bold') }"
        @click="cmd('toggleBold')"
        title="Bold"
      >
        <span class="font-bold text-sm">B</span>
      </button>

      <!-- Italic -->
      <button
        class="sel-btn"
        :class="{ active: editor.isActive('italic') }"
        @click="cmd('toggleItalic')"
        title="Italic"
      >
        <span class="italic text-sm">I</span>
      </button>

      <!-- Underline -->
      <button
        class="sel-btn"
        :class="{ active: editor.isActive('underline') }"
        @click="cmd('toggleUnderline')"
        title="Underline"
      >
        <span class="underline text-sm">U</span>
      </button>

      <!-- Strike -->
      <button
        class="sel-btn"
        :class="{ active: editor.isActive('strike') }"
        @click="cmd('toggleStrike')"
        title="Strike"
      >
        <span class="line-through text-sm">S</span>
      </button>

      <div class="sel-divider" />

      <!-- Code -->
      <button
        class="sel-btn"
        :class="{ active: editor.isActive('code') }"
        @click="cmd('toggleCode')"
        title="Code"
      >
        <svg
          class="w-[15px] h-[15px]"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        >
          <polyline points="16 18 22 12 16 6" />
          <polyline points="8 6 2 12 8 18" />
        </svg>
      </button>

      <!-- Link -->
      <LinkPopover :editor="editor" @mousedown.stop>
        <button
          class="sel-btn"
          :class="{ active: editor.isActive('link') }"
          title="Link"
          @click="lockToolbar"
        >
          <svg
            class="w-[15px] h-[15px]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          >
            <path
              d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
            />
            <path
              d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
            />
          </svg>
        </button>
      </LinkPopover>

      <!-- Color -->
      <ColorTextPopover :editor="editor" @mousedown.stop>
        <button class="sel-btn" title="Color" @click="lockToolbar">
          <span class="text-sm font-medium">A</span>
          <svg
            class="w-2.5 h-2.5 ml-0.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </ColorTextPopover>

      <div class="sel-divider" />

      <!-- Delete -->
      <button class="sel-btn" title="Delete" @click="cmd('deleteSelection')">
        <svg
          class="w-[15px] h-[15px]"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="3 6 5 6 21 6" />
          <path
            d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
          />
        </svg>
      </button>
    </div>
  </Transition>
</template>

<style scoped>
.selection-toolbar {
  @apply absolute z-50 flex items-center gap-1;
  @apply bg-popover border border-border;
  padding: 4px 6px;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  pointer-events: auto;
}

.sel-btn {
  @apply w-9 h-9 flex items-center justify-center rounded-md;
  @apply text-muted-foreground;
  @apply transition-all duration-100;
}

.sel-btn:hover {
  @apply bg-muted text-foreground;
}

.sel-btn.active {
  @apply bg-muted text-foreground;
}

.sel-btn--text {
  @apply px-2.5 w-auto gap-0.5;
}

.sel-divider {
  @apply w-px h-6 bg-border mx-1;
}

.sel-toolbar-enter-active {
  transition: opacity 150ms ease, transform 150ms ease;
}

.sel-toolbar-leave-active {
  transition: opacity 100ms ease;
}

.sel-toolbar-enter-from,
.sel-toolbar-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(4px);
}

.sel-toolbar-enter-to {
  transform: translateX(-50%) translateY(0);
}
</style>
